import { useEffect, useState, type ReactNode } from 'react';
import { RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Select, Input, BottomSheet } from '@/components/ui';
import { useSearchStore, type SearchFilters as Filters } from '@/stores/searchStore';
import {
  ANNUAL_INCOME_OPTIONS,
  BODY_TYPES,
  DONATION_METHODS,
  EDUCATION_LEVELS,
  IDENTITY_DISCLOSURE_OPTIONS,
  MARITAL_STATUSES,
  REGION_PREFECTURES,
  PREFECTURES,
  TRAVEL_EXPENSE_OPTIONS,
  BLOOD_TYPES,
  type RegionName,
} from '@kulum/shared';

interface SearchFiltersProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: () => void;
}

interface FilterSectionProps {
  title: string;
  id: string;
  isExpanded: boolean;
  onToggle: (id: string) => void;
  children: ReactNode;
}

interface PresetChipProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
}

interface ToggleOptionProps {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

const agePresets = [
  { label: '20代前半', min: 20, max: 24 },
  { label: '20代後半', min: 25, max: 29 },
  { label: '30代前半', min: 30, max: 34 },
  { label: '30代後半', min: 35, max: 39 },
  { label: '40代前半', min: 40, max: 44 },
];

const heightPresets = [
  { label: '170cm以上', min: 170 },
  { label: '175cm以上', min: 175 },
  { label: '180cm以上', min: 180 },
  { label: '185cm以上', min: 185 },
];

const sortOptions = [
  { value: '', label: 'おすすめ順' },
  { value: 'highSpec', label: 'ハイスペック順' },
  { value: 'review', label: 'レビュー順' },
  { value: 'verified', label: '認証充実順' },
  { value: 'nearby', label: '近い順' },
  { value: 'newest', label: '新着順' },
];

const annualIncomeMinOptions = [
  { value: '', label: '指定なし' },
  { value: ANNUAL_INCOME_OPTIONS[1], label: '400万円以上' },
  { value: ANNUAL_INCOME_OPTIONS[2], label: '600万円以上' },
  { value: ANNUAL_INCOME_OPTIONS[3], label: '800万円以上' },
  { value: ANNUAL_INCOME_OPTIONS[4], label: '1000万円以上' },
  { value: ANNUAL_INCOME_OPTIONS[5], label: '1500万円以上' },
  { value: ANNUAL_INCOME_OPTIONS[6], label: '2000万円以上' },
];

const reviewRatingOptions = [
  { value: '', label: '指定なし' },
  { value: '4', label: '4.0以上' },
  { value: '4.5', label: '4.5以上' },
];

function FilterSection({ title, id, isExpanded, onToggle, children }: FilterSectionProps) {
  return (
    <div className="border-b border-neutral-100 last:border-0">
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="w-full flex items-center justify-between py-3 text-left"
      >
        <span className="font-medium text-luxury-dark">{title}</span>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-neutral-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-neutral-400" />
        )}
      </button>
      {isExpanded && <div className="pb-4">{children}</div>}
    </div>
  );
}

function PresetChip({ label, isActive, onClick }: PresetChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-3 py-1.5 text-[12px] font-medium transition-colors ${
        isActive
          ? 'border-[#B87333] bg-[#F5EDE2] text-[#B87333]'
          : 'border-[#DEDBD6] bg-white text-[#666]'
      }`}
    >
      {label}
    </button>
  );
}

function ToggleOption({ label, checked, onChange }: ToggleOptionProps) {
  return (
    <label className="flex items-center justify-between rounded-2xl border border-[#DEDBD6] bg-white px-4 py-3">
      <span className="text-[14px] font-medium text-[#555]">{label}</span>
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
        className="h-4 w-4 rounded border-[#DEDBD6] accent-[#B87333]"
      />
    </label>
  );
}

export function SearchFilters({ isOpen, onClose, onApply }: SearchFiltersProps) {
  const { filters, setFilters, resetFilters } = useSearchStore();
  const [localFilters, setLocalFilters] = useState<Filters>(filters);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    sort: true,
    basic: true,
    spec: false,
    trust: false,
    policy: false,
    detail: false,
  });

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
    }
  }, [filters, isOpen]);

  const handleReset = () => {
    setLocalFilters({});
    resetFilters();
  };

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
    onClose();
  };

  const toggleSection = (section: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const updateLocalFilters = (patch: Partial<Filters>) => {
    setLocalFilters((prev) => ({ ...prev, ...patch }));
  };

  const regionOptions = [
    { value: '', label: 'すべての地域' },
    ...Object.keys(REGION_PREFECTURES).map((region) => ({
      value: region,
      label: region,
    })),
  ];

  const prefectureOptions = [
    { value: '', label: 'すべての都道府県' },
    ...PREFECTURES.map((pref) => ({
      value: pref,
      label: pref,
    })),
  ];

  const donationAreaOptions = [
    { value: '', label: '指定なし' },
    ...PREFECTURES.map((pref) => ({
      value: pref,
      label: pref,
    })),
  ];

  const bloodTypeOptions = [
    { value: '', label: 'すべて' },
    ...BLOOD_TYPES.map((type) => ({
      value: type,
      label: `${type}型`,
    })),
  ];

  const educationOptions = [
    { value: '', label: 'すべて' },
    ...EDUCATION_LEVELS.map((level) => ({
      value: level,
      label: level,
    })),
  ];

  const donationMethodOptions = [
    { value: '', label: '指定なし' },
    ...DONATION_METHODS.map((method) => ({
      value: method.value,
      label: method.label,
    })),
  ];

  const bodyTypeOptions = [
    { value: '', label: '指定なし' },
    ...BODY_TYPES.map((type) => ({
      value: type,
      label: type,
    })),
  ];

  const maritalStatusOptions = [
    { value: '', label: '指定なし' },
    ...MARITAL_STATUSES.map((status) => ({
      value: status,
      label: status,
    })),
  ];

  const travelExpenseOptions = [
    { value: '', label: '指定なし' },
    ...TRAVEL_EXPENSE_OPTIONS.filter((option) => option !== '選択してください').map((option) => ({
      value: option,
      label: option,
    })),
  ];

  const identityDisclosureOptions = [
    { value: '', label: '指定なし' },
    ...IDENTITY_DISCLOSURE_OPTIONS.map((option) => ({
      value: option,
      label: option,
    })),
  ];

  const footerContent = (
    <div className="flex gap-3 p-4 border-t border-neutral-100 bg-white">
      <Button
        variant="secondary"
        onClick={handleReset}
        leftIcon={<RotateCcw className="w-4 h-4" />}
        className="flex-1"
      >
        リセット
      </Button>
      <Button onClick={handleApply} className="flex-1">
        適用する
      </Button>
    </div>
  );

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="検索フィルター"
      height="fixed"
      footer={footerContent}
      mobileFullScreen={true}
    >
      <div className="px-4">
        <FilterSection
          title="並び順"
          id="sort"
          isExpanded={Boolean(expandedSections.sort)}
          onToggle={toggleSection}
        >
          <Select
            value={localFilters.sortBy || ''}
            onChange={(e) =>
              updateLocalFilters({
                sortBy: (e.target.value as Filters['sortBy']) || undefined,
              })
            }
            options={sortOptions}
          />
        </FilterSection>

        <FilterSection
          title="よく使う条件"
          id="basic"
          isExpanded={Boolean(expandedSections.basic)}
          onToggle={toggleSection}
        >
          <div className="space-y-4">
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="エリア"
                value={localFilters.region || ''}
                onChange={(e) =>
                  updateLocalFilters({
                    region: (e.target.value as RegionName) || undefined,
                    prefecture: undefined,
                  })
                }
                options={regionOptions}
              />
              <Select
                label="都道府県"
                value={localFilters.prefecture || ''}
                onChange={(e) =>
                  updateLocalFilters({
                    prefecture: e.target.value || undefined,
                    region: undefined,
                  })
                }
                options={prefectureOptions}
              />
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-medium text-[#666]">年齢</label>
              <div className="mb-3 flex flex-wrap gap-2">
                {agePresets.map((preset) => (
                  <PresetChip
                    key={preset.label}
                    label={preset.label}
                    isActive={localFilters.ageMin === preset.min && localFilters.ageMax === preset.max}
                    onClick={() => updateLocalFilters({ ageMin: preset.min, ageMax: preset.max })}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  placeholder="下限"
                  value={localFilters.ageMin || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      ageMin: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  min={18}
                  max={60}
                />
                <span className="text-neutral-500">〜</span>
                <Input
                  type="number"
                  placeholder="上限"
                  value={localFilters.ageMax || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      ageMax: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  min={18}
                  max={60}
                />
                <span className="text-sm text-neutral-500 whitespace-nowrap">歳</span>
              </div>
            </div>

            <div>
              <label className="mb-2 block text-[13px] font-medium text-[#666]">身長</label>
              <div className="mb-3 flex flex-wrap gap-2">
                {heightPresets.map((preset) => (
                  <PresetChip
                    key={preset.label}
                    label={preset.label}
                    isActive={localFilters.heightMin === preset.min && !localFilters.heightMax}
                    onClick={() => updateLocalFilters({ heightMin: preset.min, heightMax: undefined })}
                  />
                ))}
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  placeholder="下限"
                  value={localFilters.heightMin || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      heightMin: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  min={140}
                  max={210}
                />
                <span className="text-neutral-500">〜</span>
                <Input
                  type="number"
                  placeholder="上限"
                  value={localFilters.heightMax || ''}
                  onChange={(e) =>
                    updateLocalFilters({
                      heightMax: e.target.value ? parseInt(e.target.value, 10) : undefined,
                    })
                  }
                  min={140}
                  max={210}
                />
                <span className="text-sm text-neutral-500 whitespace-nowrap">cm</span>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="提供方法"
                value={localFilters.donationMethod || ''}
                onChange={(e) =>
                  updateLocalFilters({
                    donationMethod: (e.target.value as Filters['donationMethod']) || undefined,
                  })
                }
                options={donationMethodOptions}
              />
              <Select
                label="レビュー評価"
                value={localFilters.minReviewRating?.toString() || ''}
                onChange={(e) =>
                  updateLocalFilters({
                    minReviewRating: e.target.value ? parseFloat(e.target.value) : undefined,
                  })
                }
                options={reviewRatingOptions}
              />
            </div>
          </div>
        </FilterSection>

        <FilterSection
          title="スペック"
          id="spec"
          isExpanded={Boolean(expandedSections.spec)}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            <Select
              label="最終学歴"
              value={localFilters.educationLevel || ''}
              onChange={(e) => updateLocalFilters({ educationLevel: e.target.value || undefined })}
              options={educationOptions}
            />
            <Select
              label="年収"
              value={localFilters.annualIncomeMin || ''}
              onChange={(e) => updateLocalFilters({ annualIncomeMin: e.target.value || undefined })}
              options={annualIncomeMinOptions}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <Select
                label="体型"
                value={localFilters.bodyType || ''}
                onChange={(e) => updateLocalFilters({ bodyType: e.target.value || undefined })}
                options={bodyTypeOptions}
              />
              <Select
                label="婚姻状況"
                value={localFilters.maritalStatus || ''}
                onChange={(e) => updateLocalFilters({ maritalStatus: e.target.value || undefined })}
                options={maritalStatusOptions}
              />
            </div>
          </div>
        </FilterSection>

        <FilterSection
          title="認証・安心"
          id="trust"
          isExpanded={Boolean(expandedSections.trust)}
          onToggle={toggleSection}
        >
          <div className="space-y-2">
            <ToggleOption
              label="レビューあり"
              checked={Boolean(localFilters.hasReviews)}
              onChange={(checked) => updateLocalFilters({ hasReviews: checked || undefined })}
            />
            <ToggleOption
              label="本人認証済み"
              checked={Boolean(localFilters.identityVerified)}
              onChange={(checked) => updateLocalFilters({ identityVerified: checked || undefined })}
            />
            <ToggleOption
              label="学歴証明済み"
              checked={Boolean(localFilters.educationVerified)}
              onChange={(checked) => updateLocalFilters({ educationVerified: checked || undefined })}
            />
            <ToggleOption
              label="年収証明済み"
              checked={Boolean(localFilters.incomeVerified)}
              onChange={(checked) => updateLocalFilters({ incomeVerified: checked || undefined })}
            />
            <ToggleOption
              label="精液検査済み"
              checked={Boolean(localFilters.spermTestVerified)}
              onChange={(checked) => updateLocalFilters({ spermTestVerified: checked || undefined })}
            />
            <ToggleOption
              label="性感染症検査済み"
              checked={Boolean(localFilters.stdTestVerified)}
              onChange={(checked) => updateLocalFilters({ stdTestVerified: checked || undefined })}
            />
          </div>
        </FilterSection>

        <FilterSection
          title="提供条件"
          id="policy"
          isExpanded={Boolean(expandedSections.policy)}
          onToggle={toggleSection}
        >
          <div className="space-y-3">
            <Select
              label="提供可能エリア"
              value={localFilters.donationArea || ''}
              onChange={(e) => updateLocalFilters({ donationArea: e.target.value || undefined })}
              options={donationAreaOptions}
            />
            <Select
              label="交通費"
              value={localFilters.travelExpense || ''}
              onChange={(e) => updateLocalFilters({ travelExpense: e.target.value || undefined })}
              options={travelExpenseOptions}
            />
            <Select
              label="身元開示"
              value={localFilters.identityDisclosure || ''}
              onChange={(e) => updateLocalFilters({ identityDisclosure: e.target.value || undefined })}
              options={identityDisclosureOptions}
            />
          </div>
        </FilterSection>

        <FilterSection
          title="詳細条件"
          id="detail"
          isExpanded={Boolean(expandedSections.detail)}
          onToggle={toggleSection}
        >
          <Select
            label="血液型"
            value={localFilters.bloodType || ''}
            onChange={(e) => updateLocalFilters({ bloodType: e.target.value || undefined })}
            options={bloodTypeOptions}
          />
        </FilterSection>
      </div>
    </BottomSheet>
  );
}
